import { SpaceType } from "@components/atoms/Spacer/types";
import React, { useEffect, useRef, useState } from "react";
import { DeviceEventEmitter, ScrollView, View } from "react-native";
import { CheckBox, VDBText } from "@components/atoms";
import { useDispatch } from "react-redux";
import {
  setAuxPayloadData,
  setFiltersPayload,
  setPayloadData,
  setSectionName,
  useSearchFiltersAuxPayload,
  useSearchFiltersSlice,
  useValueFromPayload,
} from "@redux/slices/searchFilters";
import styled from "styled-components/native";
import LineViewContainer from "@scenes/requests/utils/LineView";
import { RouteProp, useRoute } from "@react-navigation/native";
import Routes from "@navigations/Routes";
import { useIsFocused } from "@react-navigation/native";
import { filterSearchFilters } from "@scenes/explore/utils";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import useScreenOptionsHooks from "../../../../hooks/useScreenOptionsHooks";
import RadioButton from "@components/atoms/RadioButton";
import useResetFilter from "../../../../hooks/useResetFilter";
import { useProductPrefix } from "../../../../hooks/useProductPrefix";
import { ProductCategory, SectionName } from "@utils/constants";
import urlEventEmitter, { URL_EVENTS } from "@utils/custom-url-event-emitter";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import { normalizeBoolean } from "../ButtonSection/Utils";
import { useTranslation } from "react-i18next";
import { useHideFiltersBasedOnAllowedStockItemsType } from "@redux/slices/globalSlice";
import { navigate } from "@navigations/utils/RootNavigation";

const JewelryTypeComponent = ({
  options = [],
  step = 1,
  selectedProduct = ProductCategory.JEWELRY,
  payloadName = [],
  sectionName = "",
  scrollRef,
  enterSecondFlow,
}: any) => {
  const mEnterSecondFlow = normalizeBoolean(enterSecondFlow);
  const { theme, typography } = useScreenOptionsHooks();
  const value = useValueFromPayload(sectionName, payloadName);
  const userInfo = useUserInfo();
  const route =
    useRoute<RouteProp<{ params: { targetJewelryType: string } }>>();
  const jewelryStyleMap = useRef(new Map<string, string[]>());
  const scrollViewRef = useRef<ScrollView>(null);
  const optionRefs = useRef<Record<string, any>>({});
  const dispatch = useDispatch();
  const productPrefix = useProductPrefix();
  const { filters } = useSearchFiltersSlice();
  const { t } = useTranslation();
  const [targetJewelryType, setTargetJewelryType] = useState<string | null>(
    null
  );
  const { enableJewelryBuilder } = useOrgSettingsSlice();

  const itemRefs = useRef<{ [key: string]: View | null }>({});

  let auxPayload = useSearchFiltersAuxPayload();

  let mOptions = options;
  if (enableJewelryBuilder && mEnterSecondFlow === true) {
    mOptions = options?.filter(
      (option: any) =>
        option?.search_payload?.[0] ===
        auxPayload[SectionName.JEWELRY]?.jewelry_styles[0]
    );
  }

  if (useHideFiltersBasedOnAllowedStockItemsType()) {
    if (
      userInfo?.hideJewelryBuilder() &&
      mOptions.find((option: any) => option?.name === "Bridal")
    ) {
      mOptions = mOptions.filter((option: any) => option?.name !== "Bridal");
    }
  }

  const findSelectedOption = (currentValue: any) => {
    return mOptions?.find((option: any) => {
      if (currentValue?.includes(option?.search_payload?.[0]))
        return currentValue?.includes(option?.search_payload?.[0]);
      else if (currentValue?.includes(option?.payload_name?.[0]))
        return currentValue?.includes(option?.payload_name?.[0]);
      else if (!currentValue || !currentValue?.length)
        return []?.every((item: any) => option.search_payload.includes(item));
    });
  };

  const selected = findSelectedOption(value);

  const allTypesOption = mOptions?.find((option: any) => {
    return option?.name === "All Types";
  });

  const isFocused = useIsFocused();
  const [categoryFilters, setCategoryFilters] = useState(
    filters[selectedProduct]
  );

  const filterSectionNames = categoryFilters.map((filter: any) => filter.name);
  const [selectedType, setSelectedType] = useState(selected || null);

  const getDefaultValues = (selectedOption: any): string[] => {
    return selectedOption
      ? auxPayload[sectionName]?.[selectedOption.search_payload?.[0]] ||
          auxPayload[sectionName]?.[selectedOption.search_payload]?.[0] ||
          []
      : [];
  };

  const [valueCheckboxes, setValueCheckboxes] = useState<string[]>(
    getDefaultValues(selected)
  );

  useEffect(() => {
    if (
      selectedType &&
      itemRefs?.current[selectedType.name] &&
      (theme.isMobile || theme.isTablet)
    ) {
      setTimeout(() => {
        try {
          itemRefs?.current[selectedType.name]?.measureLayout(
            scrollRef?.current,
            (x, y) => {
              scrollRef?.current?.scrollTo({ x: 0, y: y, animated: true });
            },
            () => {
              console.log("measureLayout error >");
            }
          );
        } catch (error) {
          console.log("error", error);
        }
      }, 100); // adjust delay if needed
    }
  }, [selectedType]);

  useEffect(() => {
    const newSelected = findSelectedOption(value);
    if (newSelected && newSelected.name !== selectedType?.name) {
      setSelectedType(newSelected);

      const newDefaultValues = getDefaultValues(newSelected);
      setValueCheckboxes(newDefaultValues);
    } else {
      if (valueCheckboxes !== getDefaultValues(selected)) {
        setValueCheckboxes(getDefaultValues(selected));
      }
    }
  }, [value]);

  useEffect(() => {
    if (route?.params?.targetJewelryType) {
      setTargetJewelryType(route.params.targetJewelryType);
    }

    const unsubscribe = urlEventEmitter.on(
      URL_EVENTS.FOCUS_JEWELRY_TYPE,
      (data) => {
        if (data.jewelryType) {
          setTargetJewelryType(data.jewelryType);
        }
      }
    );

    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (targetJewelryType && scrollViewRef.current) {
      setTimeout(() => {
        const targetOption = mOptions.find(
          (option: any) => option.name === targetJewelryType
        );

        if (targetOption) {
          setSelectedType(targetOption);

          if (typeof document !== "undefined") {
            const targetElement = document.getElementById(
              `jewelry-type-${targetJewelryType}`
            );
            if (targetElement) {
              targetElement.scrollIntoView({
                behavior: "smooth",
                block: "center",
              });

              setTimeout(() => setTargetJewelryType(null), 500);
            }
          }
        }
      }, 500);
    }
  }, [targetJewelryType, mOptions]);

  useEffect(() => {
    if (selected) {
      const defaultValues = getDefaultValues(selected);
      if (defaultValues.length > 0) {
        jewelryStyleMap.current.set(selected?.name, defaultValues);
      }
    }
  }, []);

  useEffect(() => {
    let _categoryFilters = filterSearchFilters(
      filters?.[selectedProduct],
      userInfo.user.price_mode
    );
    setCategoryFilters(_categoryFilters);
  }, [filters, userInfo.user.price_mode]);

  useEffect(() => {
    if (
      selectedType &&
      selectedType?.search_payload?.length &&
      sectionName &&
      payloadName &&
      step === 1 &&
      isFocused &&
      theme.isLargeScreen
    ) {
      const payload: any = {};
      payloadName?.forEach((name: string) => {
        payload[name] = selectedType.search_payload.flat();
      });
      dispatch(setFiltersPayload({ sectionName: sectionName, data: payload }));
      dispatch(setPayloadData({ [sectionName]: payload }));

      if (!targetJewelryType) {
        setTimeout(() => {
          applyFilters();
        }, 100);
      }
    }
  }, [selectedType, payloadName, sectionName]);

  const applyFilters = () => {
    const filterSplitStep = 2;
    dispatch(setSectionName(sectionName));
    theme.isLargeScreen || theme.isTablet
      ? navigate(Routes.FilterSplitScreen, {
          productType: selectedProduct,
          sectionName,
          filterSplitStep,
          productPrefix,
        })
      : navigate(Routes.SearchResultScreen, {
          productType: selectedProduct,
          sectionName,
          productPrefix,
        });
  };

  const modifyJewelryStyleMap = (
    keyName: string,
    valueName: string,
    mSelectedType: any
  ) => {
    DeviceEventEmitter.emit("filterSectionNameChange", {
      sectionName: sectionName,
    });
    DeviceEventEmitter.emit("selectedJewelryType", {
      sectionName: mSelectedType?.name,
    });

    if (targetJewelryType === keyName) {
      setTargetJewelryType(null);
    }

    if (keyName !== selectedType?.name) {
      jewelryStyleMap?.current?.clear();
    }
    let existingList = jewelryStyleMap.current.get(keyName) || [];

    const updatedList = existingList.includes(valueName)
      ? existingList.filter((item: string) => item !== valueName)
      : [...existingList, valueName];

    if (updatedList.length > 0) {
      jewelryStyleMap.current.set(keyName, updatedList);
    } else {
      jewelryStyleMap.current.delete(keyName);
    }

    saveToPayload(mSelectedType, sectionName, updatedList);

    setValueCheckboxes([...updatedList]);
  };

  const saveToPayload = (
    mSelectedType: any,
    mSectionName: string,
    subTypeList: any
  ) => {
    const payload: any = {};

    payloadName?.forEach((name: string) => {
      payload[name] = mSelectedType.search_payload.flat();
    });

    if (subTypeList && mSelectedType.search_payload.length > 0) {
      const subtypes = subTypeList.filter(
        (_subtype: string) => _subtype !== ""
      );
      mSelectedType.search_payload.forEach((key: string, index: number) => {
        if (index === 0) {
          payload[key] =
            index === 0 ? (subtypes.length > 0 ? subtypes : [""]) : [""];
        }
      });
    }

    auxPayload = { ...auxPayload };
    for (let i = 0; i < filterSectionNames.length; i++) {
      const filterSection = filterSectionNames[i];
      if (filterSection === mSectionName) {
        const mergedSectionPayload = {
          ...auxPayload[filterSection],
          ...payload, // Append new data
        };

        const sectionFilters = categoryFilters?.find(
          (s: any) => s?.name === mSectionName
        )?.filters;

        if (Array.isArray(sectionFilters)) {
          sectionFilters.forEach((f: any) => {
            const deps = f?.dependencies;
            if (!Array.isArray(deps) || deps.length === 0) return;

            const isMet = deps.some((dep: any) => {
              const val = mergedSectionPayload?.[dep?.key];
              if (val === undefined || val === null) return false;
              if (Array.isArray(val)) return val.includes(dep?.value);
              return val === dep?.value;
            });

            if (!isMet && Array.isArray(f?.payload_name)) {
              f.payload_name.forEach((pName: string) => {
                if (pName) delete mergedSectionPayload[pName];
              });
            }
          });
        }

        auxPayload[filterSection] = mergedSectionPayload;
      } else {
        auxPayload[filterSection] = {};
      }
    }
    dispatch(setAuxPayloadData(auxPayload));
    // dispatch(setPayloadData(auxData));
  };

  useResetFilter(() => {
    setSelectedType(allTypesOption);
    jewelryStyleMap?.current?.clear();

    const auxData: any = {};
    for (let i = 0; i < filterSectionNames.length; i++) {
      const filterSection = filterSectionNames[i];
      if (filterSection === sectionName) {
        auxData[filterSection] = {};
      }
    }
    dispatch(setAuxPayloadData(auxData));
  });

  return (
    <ScrollView ref={scrollViewRef}>
      {mOptions.map((item: any, index: number) => (
        <React.Fragment key={index}>
          <Overlay
            id={`jewelry-type-${item?.name}`}
            index={index}
            optionsLength={mOptions.length}
            ref={(ref: any) => {
              if (ref) {
                optionRefs.current[item?.name] = ref;
              }
            }}
            onPress={() => {
              setSelectedType(item);
              modifyJewelryStyleMap(item?.name, "", item);
            }}
          >
            <View ref={(ref: any) => (itemRefs.current[item.name] = ref)}>
              <RadioContainer>
                <RadioButton
                  label={t(item?.label) || t(item?.name)}
                  checked={selectedType?.name === item?.name}
                  labelColor={
                    selectedType?.name === item?.name
                      ? theme["core-05"]
                      : theme["core-06"]
                  }
                  styles={
                    selectedType?.name === item?.name
                      ? typography["Minor-M"]
                      : typography["Minor-R"]
                  }
                  onPress={() => {
                    setSelectedType(item);
                    modifyJewelryStyleMap(item?.name, "", item);
                  }}
                />
              </RadioContainer>
            </View>
          </Overlay>

          {item?.options !== undefined &&
            selectedType?.name === item?.name &&
            item?.options.map((_item: any) => {
              const isChecked =
                valueCheckboxes?.includes(_item.name) ||
                valueCheckboxes.includes(_item.search_payload?.[0]);

              return (
                <React.Fragment key={`${item?.name}-${_item.name}`}>
                  <LineViewContainer background="core-03" marginLeft={false} />
                  <OverlaySubtype
                    onPress={() => {
                      modifyJewelryStyleMap(
                        item?.name,
                        _item.search_payload?.[0],
                        item
                      );
                    }}
                  >
                    <CheckBox
                      fillColor={theme["knob-bor"]}
                      checkBoxSize={20}
                      checkedFunction={() =>
                        modifyJewelryStyleMap(
                          item?.name,
                          _item.search_payload?.[0],
                          item
                        )
                      }
                      isChecked={isChecked}
                    />
                    <VDBText
                      type={
                        isChecked
                          ? typography["Minor-M"]
                          : typography["Minor-R"]
                      }
                      color={isChecked ? theme["core-05"] : theme["core-06"]}
                    >
                      {t(_item.label)}
                    </VDBText>
                  </OverlaySubtype>
                </React.Fragment>
              );
            })}
          {index !== mOptions?.length - 1 && (
            <LineViewContainer background="core-03" marginLeft={false} />
          )}
        </React.Fragment>
      ))}
    </ScrollView>
  );
};

const Overlay = styled.Pressable<{
  index: number;
  optionsLength: number;
}>`
  padding-top: ${({ index }: { index: number }) =>
    index === 0 ? "unset" : SpaceType.ExtraSmall};
  padding-bottom: ${({
    index,
    optionsLength,
  }: {
    index: number;
    optionsLength: number;
  }) => (index === optionsLength - 1 ? "unset" : SpaceType.ExtraSmall)};
  padding-left: ${SpaceType.Double}px;
  padding-right: ${SpaceType.Double}px;
`;

const OverlaySubtype = styled.Pressable`
  padding-top: ${SpaceType.Double}px;
  padding-bottom: ${SpaceType.Double}px;
  padding-left: 48px;
  padding-right: ${SpaceType.Double}px;
  justify-content: start;
  align-items: center;
  flex-direction: row;
  gap: ${SpaceType.Basic}px;
`;

const RadioContainer = styled.View`
  padding-top: 0px;
  padding-bottom: 0px;
`;

export default React.memo(JewelryTypeComponent, (prevProps, nextProps) => {
  return (
    prevProps.options === nextProps.options &&
    prevProps.step === nextProps.step &&
    prevProps.selectedProduct === nextProps.selectedProduct &&
    prevProps.payloadName === nextProps.payloadName &&
    prevProps.sectionName === nextProps.sectionName &&
    prevProps.selectedStyle === nextProps.selectedStyle &&
    JSON.stringify(
      useValueFromPayload(prevProps.sectionName, prevProps.payloadName)
    ) ===
      JSON.stringify(
        useValueFromPayload(nextProps.sectionName, nextProps.payloadName)
      )
  );
});
